import { useEffect, useState } from 'preact/hooks';
import { timeago } from 'shared/dateutil';
import { rpx, RpxResponse } from 'client/lib/rpx-client';
import { LoadingIndicator } from '@components/loading-indicator';
import { IcoAcademicCap } from '@components/icons';
import { URLS } from 'shared/urls';
import { genericDiscussionCategoryIds } from 'shared/consts';

type Usage = RpxResponse<typeof rpx.mediaManager.getFileUsage>;
type Course = Usage['courses'][number];
type Location = Course['locations'][number];
type Kind = Location['kinds'][number];

const KIND_LABELS: Record<Kind, string> = {
  course_image: 'Course image',
  course_banner: 'Course banner',
  meeting_slides: 'Meeting slides',
  meeting_recording: 'Meeting recording',
  comment_attachment: 'Comment attachment',
  lesson_media: 'Lesson media',
  assessment_question_file: 'Quiz question',
  assessment_question_correct: 'Quiz feedback (correct)',
  assessment_question_incorrect: 'Quiz feedback (incorrect)',
  assessment_choice: 'Quiz choice',
  assignment_question: 'Assignment question',
};

export function UsagePanel({ fileId }: { fileId: string }) {
  const [data, setData] = useState<Usage | undefined>(undefined);
  const [error, setError] = useState<string | undefined>(undefined);

  useEffect(() => {
    let cancelled = false;
    setData(undefined);
    setError(undefined);
    rpx.mediaManager
      .getFileUsage({ fileId })
      .then((res) => {
        if (!cancelled) {
          setData(res);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err?.message || 'Failed to load usage.');
        }
      });
    return () => {
      cancelled = true;
    };
  }, [fileId]);

  return (
    <section class="border-t border-gray-200 bg-white">
      <header class="px-5 py-3 flex items-center justify-between gap-3">
        <h3 class="text-sm font-semibold text-gray-900 inline-flex items-center gap-2">
          <IcoAcademicCap class="size-4 opacity-80" />
          Where it's used
        </h3>
        {data?.lastRefreshedAt && (
          <span class="text-xs text-gray-500">
            Last refreshed {timeago(data.lastRefreshedAt, ' ago')}
          </span>
        )}
      </header>

      <div class="px-5 pb-4 text-sm text-gray-700">
        {error ? (
          <p class="text-red-600 text-xs">{error}</p>
        ) : !data ? (
          <div class="py-3">
            <LoadingIndicator />
          </div>
        ) : data.totalCourseCount === 0 ? (
          <div class="text-gray-500 text-xs space-y-1">
            <p>No course uses found.</p>
            <p>Images pasted into lessons or comments aren't included in this check.</p>
          </div>
        ) : (
          <>
            {data.truncated && (
              <p class="mb-3 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">
                Used in {data.totalCourseCount} courses — showing the first {data.courses.length}.
              </p>
            )}
            <ul class="flex flex-col gap-3">
              {data.courses.map((c) => (
                <CourseGroup key={c.course.id} course={c} />
              ))}
            </ul>
          </>
        )}
      </div>
    </section>
  );
}

function CourseGroup({ course }: { course: Course }) {
  return (
    <li class="border border-gray-200 rounded-md">
      <a
        href={URLS.guide.course({ courseId: course.course.id, isProduct: course.course.isProduct })}
        target="_blank"
        rel="noopener noreferrer"
        class="block px-3 py-2 font-medium text-indigo-700 hover:bg-gray-50 text-sm truncate"
      >
        {course.course.title}
      </a>
      {course.locations.length > 0 && (
        <ul class="divide-y divide-gray-100">
          {course.locations.map((loc, i) => (
            <LocationRow key={i} course={course.course} loc={loc} />
          ))}
        </ul>
      )}
    </li>
  );
}

function locationHref(
  course: { id: string; title: string; isProduct: boolean },
  loc: Location,
): string {
  // Comment attachment — link straight to the discussion (or the lesson
  // discussion thread) with the comment id as a query param.
  if (loc.discussionId && loc.commentId) {
    if (loc.lessonId) {
      return URLS.student.comment({
        course: { id: course.id, title: course.title, isProduct: course.isProduct },
        lesson: { id: loc.lessonId, title: loc.lessonTitle || '' },
        commentId: loc.commentId,
      });
    }
    return URLS.student.discussionComment({
      course: { id: course.id, title: course.title, isProduct: course.isProduct },
      categoryId: loc.categoryId || genericDiscussionCategoryIds.all,
      discussionId: loc.discussionId,
      commentId: loc.commentId,
    });
  }
  if (loc.lessonId) {
    return URLS.guide.lesson({
      courseId: course.id,
      isProduct: course.isProduct,
      lessonId: loc.lessonId,
    });
  }
  if (loc.meetingId) {
    return URLS.guide.meeting({ courseId: course.id, meetingId: loc.meetingId });
  }
  return URLS.guide.course({ courseId: course.id, isProduct: course.isProduct });
}

function LocationRow({
  course,
  loc,
}: {
  course: { id: string; title: string; isProduct: boolean };
  loc: Location;
}) {
  const label = loc.discussionId
    ? loc.lessonTitle
      ? `${loc.lessonTitle} — comment`
      : 'Discussion comment'
    : loc.lessonId
      ? loc.lessonTitle || 'Lesson'
      : loc.meetingId
        ? loc.meetingTitle || 'Meeting'
        : 'Course-level';
  const href = locationHref(course, loc);

  return (
    <li class="px-3 py-2 flex flex-wrap items-center gap-2 text-xs">
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        class="text-gray-800 hover:text-indigo-700 hover:underline truncate"
      >
        {label}
      </a>
      <div class="flex flex-wrap gap-1">
        {loc.kinds.map((k) => (
          <span
            key={k}
            class="inline-flex items-center px-1.5 py-0.5 rounded bg-gray-100 text-gray-600 text-[10px] tracking-wide uppercase"
          >
            {KIND_LABELS[k]}
          </span>
        ))}
      </div>
    </li>
  );
}
